/**
 * Admin — Security/Authentication (W1b-09 + W1b-10 backend-wired UI)
 *
 * Two tabs: Authentication (password + MFA) and Session & Access
 * (session policy + rate-limit tier + IP zones). All forms write through
 * the real /admin/security/* API surface added in Wave 1b Stage 2.
 *
 * v1 scope (intentional simplifications, documented in the closure note):
 *   - Password policy: 9 NIST-framed knobs without the live tester (the
 *     tester is a v1.1 polish item; the form fully exercises the API).
 *   - MFA config: enforced + allowedMethods (4-method picker) + grace
 *     period. ITAR auto-removal of SMS/Email is W1b-03 cascade-driven and
 *     not visible at this surface yet.
 *   - SSO IS NOT HERE ANY MORE (Foundations 2 / B2d-T4, decisions.md
 *     2026-09-13 #17). The tenant's SSO connections are a Users-rail noun
 *     and live on Users > SSO.
 *   - Session policy: 4 cross-validated knobs.
 *   - Rate-limit tier: 6-option Select (5 built-ins + custom).
 *   - IP zones: list + add (name/CIDR/action) + delete + reorder via
 *     up/down buttons.
 *
 * The previous mock-data-driven UI lived at this same route; the relevant
 * design ideas are preserved for a future v1.1 polish pass — see
 * docs/waves/wave-1b/W1b-09-10-status.md.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import {
  KeyRound,
  ShieldCheck,
  Link2,
  Plus,
  Trash2,
  Globe,
  Gauge,
  Clock,
  ArrowUp,
  ArrowDown,
  Star,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { SectionCard, SettingField } from '@/components/admin';
import { apiClient } from '@/lib/api-client';

export const Route = createFileRoute('/admin/security/')({
  component: SecurityPage,
});

// ─── Types (mirror server response shapes) ─────────────────────────────

interface PasswordPolicy {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumber: boolean;
  requireSymbol: boolean;
  maxAgeDays: number;
  rotationCount: number;
  lockoutFailedAttempts: number;
  lockoutMinutes: number;
}

type MfaMethod = 'totp' | 'webauthn' | 'sms' | 'email';

interface MfaConfig {
  enforced: boolean;
  allowedMethods: MfaMethod[];
  gracePeriodDays: number;
}

interface SessionPolicy {
  idleTimeoutMinutes: number;
  absoluteTimeoutHours: number;
  refreshRotation: boolean;
  concurrentSessionLimit: number;
}

type RateLimitTier =
  | 'unlimited'
  | 'high'
  | 'standard'
  | 'low'
  | 'restricted'
  | 'custom';

interface IpZone {
  id: string;
  name: string;
  cidr: string;
  action: 'allow' | 'deny';
  sortOrder: number;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

interface PolicyResponse {
  passwordPolicy: PasswordPolicy;
  mfaConfig: MfaConfig;
  sessionPolicy: SessionPolicy;
  rateLimitTier: RateLimitTier;
  rateLimitCustom: unknown;
  updatedBy: string | null;
  updatedAt: string;
}

// ─── Page wrapper ──────────────────────────────────────────────────────

function SecurityPage() {
  return (
    <div className="max-w-5xl mx-auto p-6 space-y-5">
      <div>
        <h1 className="text-xl font-semibold">Security</h1>
        <p className="text-xs text-muted-foreground mt-1">
          Password policy, MFA, session lifetimes, IP zones,
          and rate-limit tiers.
        </p>
      </div>

      <Tabs defaultValue="auth" className="space-y-4">
        <TabsList>
          <TabsTrigger value="auth" className="text-xs">
            Authentication
          </TabsTrigger>
          <TabsTrigger value="session" className="text-xs">
            Session &amp; Access
          </TabsTrigger>
        </TabsList>

        <TabsContent value="auth" className="space-y-5">
          <AuthenticationTab />
        </TabsContent>
        <TabsContent value="session" className="space-y-5">
          <SessionAccessTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Authentication tab
// ═══════════════════════════════════════════════════════════════════════

function AuthenticationTab() {
  const [policy, setPolicy] = useState<PolicyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.get<PolicyResponse>(
        '/admin/security/policy'
      );
      setPolicy(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load security policy';
      toast.error('Failed to load security policy');
      setError(msg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Bug #1050 fix — distinguish loading vs. error vs. data.
  // Previously the page rendered "Loading security policy…" indefinitely
  // when the underlying GET failed (e.g. 401 token-expiry race, 500, or
  // network failure). The cycle-3.6 vision judge correctly flagged the
  // page as never rendering its real content. Now an error renders an
  // actionable retry button + the upstream message; loading is only
  // shown while the request is genuinely in flight.
  if (loading) {
    return (
      <div
        data-testid="security-auth-loading"
        className="flex items-center gap-2 text-xs text-muted-foreground p-4"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading security policy…
      </div>
    );
  }

  if (error || !policy) {
    return (
      <div
        data-testid="security-auth-error"
        className="flex flex-col gap-3 p-4 border border-red-500/40 bg-red-500/5 rounded-md"
      >
        <div className="text-xs font-medium text-red-700 dark:text-red-300">
          Failed to load security policy
        </div>
        {error && (
          <div className="text-[11px] text-red-600 dark:text-red-400 font-mono">
            {error}
          </div>
        )}
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs w-fit"
          onClick={refresh}
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <>
      <PasswordPolicySection
        value={policy.passwordPolicy}
        onSaved={(next) =>
          setPolicy((p) => (p ? { ...p, passwordPolicy: next } : p))
        }
      />
      <MfaSection
        value={policy.mfaConfig}
        onSaved={(next) =>
          setPolicy((p) => (p ? { ...p, mfaConfig: next } : p))
        }
      />
    </>
  );
}

// ─── Password policy ───────────────────────────────────────────────────

function PasswordPolicySection({
  value,
  onSaved,
}: {
  value: PasswordPolicy;
  onSaved: (next: PasswordPolicy) => void;
}) {
  const [draft, setDraft] = useState<PasswordPolicy>(value);
  const [saving, setSaving] = useState(false);

  useEffect(() => setDraft(value), [value]);

  async function save() {
    if (draft.minLength < 8) {
      toast.error('Minimum password length must be at least 8 (NIST SP 800-63B).');
      return;
    }
    try {
      setSaving(true);
      const res = await apiClient.put<{
        success: true;
        passwordPolicy: PasswordPolicy;
      }>('/admin/security/password-policy', draft);
      onSaved(res.passwordPolicy);
      toast.success('Password policy updated');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <SectionCard
      title="Password Policy"
      icon={KeyRound}
      description="Applies to local accounts. NIST SP 800-63B baseline."
      actions={
        <Button
          size="sm"
          className="h-7 text-xs"
          onClick={save}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      }
    >
      <div className="grid grid-cols-2 gap-x-8 gap-y-1">
        <SettingField
          label="Minimum length"
          description="NIST recommends ≥8. Admin accounts strongly prefer ≥14."
        >
          <Input
            type="number"
            value={draft.minLength}
            onChange={(e) =>
              setDraft({
                ...draft,
                minLength: parseInt(e.target.value, 10) || 8,
              })
            }
            className="h-8 w-24 text-xs"
            min={8}
            max={128}
          />
        </SettingField>

        <SettingField label="Require uppercase (A-Z)">
          <Switch
            checked={draft.requireUppercase}
            onCheckedChange={(v) =>
              setDraft({ ...draft, requireUppercase: v })
            }
            className="scale-75"
          />
        </SettingField>

        <SettingField label="Require lowercase (a-z)">
          <Switch
            checked={draft.requireLowercase}
            onCheckedChange={(v) =>
              setDraft({ ...draft, requireLowercase: v })
            }
            className="scale-75"
          />
        </SettingField>

        <SettingField label="Require number (0-9)">
          <Switch
            checked={draft.requireNumber}
            onCheckedChange={(v) => setDraft({ ...draft, requireNumber: v })}
            className="scale-75"
          />
        </SettingField>

        <SettingField label="Require symbol (!@#$…)">
          <Switch
            checked={draft.requireSymbol}
            onCheckedChange={(v) => setDraft({ ...draft, requireSymbol: v })}
            className="scale-75"
          />
        </SettingField>

        <SettingField
          label="Max age (days)"
          description="0 = never expires."
        >
          <Input
            type="number"
            value={draft.maxAgeDays}
            onChange={(e) =>
              setDraft({
                ...draft,
                maxAgeDays: parseInt(e.target.value, 10) || 0,
              })
            }
            className="h-8 w-24 text-xs"
            min={0}
          />
        </SettingField>

        <SettingField label="Password history (rotation count)">
          <Input
            type="number"
            value={draft.rotationCount}
            onChange={(e) =>
              setDraft({
                ...draft,
                rotationCount: parseInt(e.target.value, 10) || 0,
              })
            }
            className="h-8 w-24 text-xs"
            min={0}
          />
        </SettingField>

        <SettingField label="Lockout after failed attempts">
          <Input
            type="number"
            value={draft.lockoutFailedAttempts}
            onChange={(e) =>
              setDraft({
                ...draft,
                lockoutFailedAttempts: parseInt(e.target.value, 10) || 5,
              })
            }
            className="h-8 w-24 text-xs"
            min={3}
          />
        </SettingField>

        <SettingField label="Lockout duration (minutes)">
          <Input
            type="number"
            value={draft.lockoutMinutes}
            onChange={(e) =>
              setDraft({
                ...draft,
                lockoutMinutes: parseInt(e.target.value, 10) || 15,
              })
            }
            className="h-8 w-24 text-xs"
            min={1}
          />
        </SettingField>
      </div>
    </SectionCard>
  );
}

// ─── MFA ───────────────────────────────────────────────────────────────

function MfaSection({
  value,
  onSaved,
}: {
  value: MfaConfig;
  onSaved: (next: MfaConfig) => void;
}) {
  const [draft, setDraft] = useState<MfaConfig>(value);
  const [saving, setSaving] = useState(false);

  useEffect(() => setDraft(value), [value]);

  function toggleMethod(m: MfaMethod) {
    if (draft.allowedMethods.includes(m)) {
      setDraft({
        ...draft,
        allowedMethods: draft.allowedMethods.filter((x) => x !== m),
      });
    } else {
      setDraft({ ...draft, allowedMethods: [...draft.allowedMethods, m] });
    }
  }

  async function save() {
    if (draft.enforced && draft.allowedMethods.length === 0) {
      toast.error(
        'Enforced MFA requires at least one allowed method.'
      );
      return;
    }
    try {
      setSaving(true);
      const res = await apiClient.put<{ success: true; mfaConfig: MfaConfig }>(
        '/admin/security/mfa-config',
        draft
      );
      onSaved(res.mfaConfig);
      toast.success('MFA config updated');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <SectionCard
      title="Multi-Factor Authentication"
      icon={ShieldCheck}
      description="A second factor blocks ~99% of credential-based attacks. CMMC IA.L2-3.5.3."
      actions={
        <Button size="sm" className="h-7 text-xs" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      }
    >
      <SettingField
        label="MFA required"
        description="Enforce MFA for all local accounts."
      >
        <Switch
          checked={draft.enforced}
          onCheckedChange={(v) => setDraft({ ...draft, enforced: v })}
          className="scale-75"
        />
      </SettingField>

      <Separator className="my-3" />

      <div>
        <Label className="text-xs font-semibold">Allowed methods</Label>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {(['totp', 'webauthn', 'sms', 'email'] as MfaMethod[]).map((m) => {
            const active = draft.allowedMethods.includes(m);
            const strong = m === 'totp' || m === 'webauthn';
            return (
              <button
                key={m}
                onClick={() => toggleMethod(m)}
                className={`flex items-center gap-2 p-2.5 border rounded text-left text-xs transition-colors ${
                  active
                    ? 'border-primary/40 bg-primary/5'
                    : 'hover:bg-muted/30'
                }`}
              >
                <span className="font-medium uppercase">{m}</span>
                <Badge
                  variant="outline"
                  className={`h-4 text-[9px] px-1.5 ${
                    strong
                      ? 'border-emerald-500/40 text-emerald-600'
                      : 'border-amber-500/40 text-amber-600'
                  }`}
                >
                  {strong ? 'strong' : 'weak'}
                </Badge>
                {active && <CheckCircle2 className="h-3 w-3 ml-auto text-primary" />}
              </button>
            );
          })}
        </div>
      </div>

      <Separator className="my-3" />

      <SettingField
        label="Grace period (days)"
        description="Days a new user has to enroll before being locked out."
      >
        <Input
          type="number"
          value={draft.gracePeriodDays}
          onChange={(e) =>
            setDraft({
              ...draft,
              gracePeriodDays: parseInt(e.target.value, 10) || 0,
            })
          }
          className="h-8 w-24 text-xs"
          min={0}
        />
      </SettingField>
    </SectionCard>
  );
}

// ─── SSO providers — REMOVED (Foundations 2 / B2d-T4) ──────────────────
//
// decisions.md 2026-09-13 #17: *"the Admin > Security > Auth SSO section is
// REMOVED outright, not kept as a read-only view."*
//
// It was the SECOND surface on one noun. `tenant_sso_providers` is owned by the
// Users rail (docs/system/entities.md), and this section's form wrote it
// directly — a name, a protocol and an opaque `metadata` blob that carried the
// OIDC client secret and had to be stripped on the way out by a key-name
// denylist. The tenant configures SSO on **Users > SSO** now, against typed
// columns with the secret encrypted at rest, a test-connection button and
// just-in-time provisioning; the four Admin endpoints this section called are
// deleted from the API in the same change, so leaving a read-only view here
// would render an empty card against a 404. Their paths are deliberately not
// quoted here: `routes/users/__tests__/-sso-clarity.test.tsx` scans this file
// for them, and a comment that names one reads exactly like the code coming
// back.

// ═══════════════════════════════════════════════════════════════════════
// Session & Access tab
// ═══════════════════════════════════════════════════════════════════════

function SessionAccessTab() {
  const [policy, setPolicy] = useState<PolicyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.get<PolicyResponse>(
        '/admin/security/policy'
      );
      setPolicy(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load session policy';
      toast.error('Failed to load session policy');
      setError(msg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Bug #1050 fix — sister of AuthenticationTab. Distinguish loading
  // from error so the page can't get stuck rendering the spinner when
  // the GET fails.
  if (loading) {
    return (
      <div
        data-testid="security-session-loading"
        className="flex items-center gap-2 text-xs text-muted-foreground p-4"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading session policy…
      </div>
    );
  }

  if (error || !policy) {
    return (
      <div
        data-testid="security-session-error"
        className="flex flex-col gap-3 p-4 border border-red-500/40 bg-red-500/5 rounded-md"
      >
        <div className="text-xs font-medium text-red-700 dark:text-red-300">
          Failed to load session policy
        </div>
        {error && (
          <div className="text-[11px] text-red-600 dark:text-red-400 font-mono">
            {error}
          </div>
        )}
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs w-fit"
          onClick={refresh}
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <>
      <SessionPolicySection
        value={policy.sessionPolicy}
        onSaved={(next) =>
          setPolicy((p) => (p ? { ...p, sessionPolicy: next } : p))
        }
      />
      <RateLimitSection
        tier={policy.rateLimitTier}
        onSaved={(t) => setPolicy((p) => (p ? { ...p, rateLimitTier: t } : p))}
      />
      <IpZonesSection />
    </>
  );
}

// ─── Session policy ────────────────────────────────────────────────────

function SessionPolicySection({
  value,
  onSaved,
}: {
  value: SessionPolicy;
  onSaved: (next: SessionPolicy) => void;
}) {
  const [draft, setDraft] = useState<SessionPolicy>(value);
  const [saving, setSaving] = useState(false);

  useEffect(() => setDraft(value), [value]);

  async function save() {
    if (draft.idleTimeoutMinutes < 10) {
      toast.error('Idle timeout must be at least 10 minutes.');
      return;
    }
    if (draft.idleTimeoutMinutes > draft.absoluteTimeoutHours * 60) {
      toast.error(
        'Idle timeout cannot exceed absolute timeout (otherwise idle never triggers).'
      );
      return;
    }
    try {
      setSaving(true);
      const res = await apiClient.put<{
        success: true;
        sessionPolicy: SessionPolicy;
      }>('/admin/security/session-policy', draft);
      onSaved(res.sessionPolicy);
      toast.success('Session policy updated');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <SectionCard
      title="Session policy"
      icon={Clock}
      description="HIPAA §164.312(a)(2)(iii) requires automatic logoff after a period of inactivity."
      actions={
        <Button size="sm" className="h-7 text-xs" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      }
    >
      <div className="grid grid-cols-2 gap-x-8 gap-y-1">
        <SettingField
          label="Idle timeout (minutes)"
          description="Auto-logout after inactivity. HIPAA §164.312(a)(2)(iii)."
        >
          <Input
            type="number"
            value={draft.idleTimeoutMinutes}
            onChange={(e) =>
              setDraft({
                ...draft,
                idleTimeoutMinutes: parseInt(e.target.value, 10) || 60,
              })
            }
            className="h-8 w-24 text-xs"
            min={10}
            max={1440}
          />
        </SettingField>

        <SettingField
          label="Absolute timeout (hours)"
          description="Hard cap regardless of activity."
        >
          <Input
            type="number"
            value={draft.absoluteTimeoutHours}
            onChange={(e) =>
              setDraft({
                ...draft,
                absoluteTimeoutHours: parseInt(e.target.value, 10) || 24,
              })
            }
            className="h-8 w-24 text-xs"
            min={1}
            max={720}
          />
        </SettingField>

        <SettingField
          label="Refresh-token rotation"
          description="Issue a new refresh token on every refresh."
        >
          <Switch
            checked={draft.refreshRotation}
            onCheckedChange={(v) =>
              setDraft({ ...draft, refreshRotation: v })
            }
            className="scale-75"
          />
        </SettingField>

        <SettingField
          label="Concurrent sessions per user"
          description="0 = unlimited."
        >
          <Input
            type="number"
            value={draft.concurrentSessionLimit}
            onChange={(e) =>
              setDraft({
                ...draft,
                concurrentSessionLimit: parseInt(e.target.value, 10) || 0,
              })
            }
            className="h-8 w-24 text-xs"
            min={0}
            max={100}
          />
        </SettingField>
      </div>
    </SectionCard>
  );
}

// ─── Rate-limit tier ───────────────────────────────────────────────────

function RateLimitSection({
  tier,
  onSaved,
}: {
  tier: RateLimitTier;
  onSaved: (next: RateLimitTier) => void;
}) {
  const [draft, setDraft] = useState<RateLimitTier>(tier);
  const [saving, setSaving] = useState(false);

  useEffect(() => setDraft(tier), [tier]);

  async function save() {
    try {
      setSaving(true);
      await apiClient.put('/admin/security/rate-limit-tier', { tier: draft });
      onSaved(draft);
      toast.success(`Rate-limit tier set to ${draft}`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <SectionCard
      title="Rate-limit tier"
      icon={Gauge}
      description="5 built-in tiers + custom. Enforcement at request time arrives in Wave 2."
      actions={
        <Button size="sm" className="h-7 text-xs" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      }
    >
      <SettingField label="Tier" description="Higher tiers grant more headroom; restricted is for paused tenants.">
        <Select value={draft} onValueChange={(v) => setDraft(v as RateLimitTier)}>
          <SelectTrigger className="h-8 w-48 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="unlimited" className="text-xs">
              Unlimited
            </SelectItem>
            <SelectItem value="high" className="text-xs">
              High
            </SelectItem>
            <SelectItem value="standard" className="text-xs">
              Standard (default)
            </SelectItem>
            <SelectItem value="low" className="text-xs">
              Low
            </SelectItem>
            <SelectItem value="restricted" className="text-xs">
              Restricted
            </SelectItem>
            <SelectItem value="custom" className="text-xs">
              Custom
            </SelectItem>
          </SelectContent>
        </Select>
      </SettingField>
    </SectionCard>
  );
}

// ─── IP zones ──────────────────────────────────────────────────────────

function IpZonesSection() {
  const [zones, setZones] = useState<IpZone[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.get<{ zones: IpZone[] }>(
        '/admin/security/ip-zones'
      );
      setZones(data.zones);
    } catch (err) {
      toast.error('Failed to load IP zones');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function move(id: string, dir: -1 | 1) {
    const idx = zones.findIndex((z) => z.id === id);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= zones.length) return;
    const next = [...zones];
    [next[idx], next[target]] = [next[target], next[idx]];
    setZones(next); // optimistic
    try {
      await apiClient.post('/admin/security/ip-zones/reorder', {
        order: next.map((z) => z.id),
      });
      refresh();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to reorder';
      toast.error(msg);
      refresh();
    }
  }

  async function deleteZone(id: string, name: string) {
    if (!window.confirm(`Remove IP zone "${name}"?`)) return;
    try {
      await apiClient.delete(`/admin/security/ip-zones/${id}`);
      toast.success('Zone removed');
      refresh();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to delete';
      toast.error(msg);
    }
  }

  return (
    <SectionCard
      title="IP allow / deny zones"
      icon={Globe}
      description="Ordered list, first match wins by sort order. Enforcement at request time is Wave 2/3."
      actions={
        <Button
          size="sm"
          className="h-7 text-xs gap-1.5"
          onClick={() => setAdding(true)}
        >
          <Plus className="h-3 w-3" />
          Add zone
        </Button>
      }
    >
      {loading ? (
        <div className="text-xs text-muted-foreground py-2">Loading…</div>
      ) : zones.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center border border-dashed rounded-md">
          <Globe className="h-5 w-5 text-muted-foreground/50 mb-2" />
          <div className="text-xs font-medium">No IP zones configured</div>
          <p className="text-[10px] text-muted-foreground mt-1 max-w-xs">
            All addresses are allowed (default-allow when no zones exist).
          </p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {zones.map((z, i) => (
            <div
              key={z.id}
              className="flex items-center gap-2 border rounded-md p-2.5"
            >
              <div className="flex flex-col">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0"
                  disabled={i === 0}
                  onClick={() => move(z.id, -1)}
                  title="Move up"
                >
                  <ArrowUp className="h-2.5 w-2.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0"
                  disabled={i === zones.length - 1}
                  onClick={() => move(z.id, 1)}
                  title="Move down"
                >
                  <ArrowDown className="h-2.5 w-2.5" />
                </Button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium">{z.name}</span>
                  <Badge
                    variant="outline"
                    className={`h-4 text-[9px] px-1.5 ${
                      z.action === 'allow'
                        ? 'border-emerald-500/40 text-emerald-600'
                        : 'border-red-500/40 text-red-600'
                    }`}
                  >
                    {z.action}
                  </Badge>
                  <span className="text-[10px] font-mono text-muted-foreground">
                    {z.cidr}
                  </span>
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5 tabular-nums">
                  Sort {z.sortOrder}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => deleteZone(z.id, z.name)}
                title="Remove zone"
              >
                <Trash2 className="h-3 w-3 text-red-500" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {adding && (
        <AddIpZoneDialog
          onClose={() => setAdding(false)}
          onCreated={() => {
            setAdding(false);
            refresh();
          }}
        />
      )}
    </SectionCard>
  );
}

function AddIpZoneDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [cidr, setCidr] = useState('');
  const [action, setAction] = useState<'allow' | 'deny'>('allow');
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    if (!name.trim() || !cidr.trim()) {
      toast.error('Name and CIDR required');
      return;
    }
    try {
      setSubmitting(true);
      await apiClient.post('/admin/security/ip-zones', {
        name: name.trim(),
        cidr: cidr.trim(),
        action,
        isEnabled: true,
      });
      toast.success('Zone added');
      onCreated();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to create';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-background border rounded-lg p-5 w-[420px] space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-sm font-semibold">Add IP zone</div>
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Name
          </Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Office VPN"
            className="h-8 text-xs"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
            CIDR
          </Label>
          <Input
            value={cidr}
            onChange={(e) => setCidr(e.target.value)}
            placeholder="10.0.0.0/8 or 2001:db8::/32"
            className="h-8 text-xs font-mono"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Action
          </Label>
          <Select value={action} onValueChange={(v) => setAction(v as 'allow' | 'deny')}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="allow" className="text-xs">
                Allow
              </SelectItem>
              <SelectItem value="deny" className="text-xs">
                Deny
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="h-8 text-xs"
            onClick={submit}
            disabled={submitting}
          >
            {submitting ? 'Adding…' : 'Add'}
          </Button>
        </div>
      </div>
    </div>
  );
}
